import {Box, Link, Text} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {VALUES} from '../../constants/values'
import type {EventActor$key} from './__generated__/EventActor.graphql'
import {GhostActor} from './GhostActor'
import {userHovercardPath} from '@github-ui/paths'
import {GitHubAvatar} from '@github-ui/github-avatar'

type EventActorProps = {
  actor: EventActor$key | null
} & SharedProps

type EventActorPropsInternal = {
  actor: EventActor$key
} & SharedProps

type SharedProps = {
  largeAvatar?: boolean
  showAvatarOnly: boolean
  leadingIconOnTop?: boolean
}

type EventActorBaseProps = {
  login?: string
  avatarUrl: string
} & SharedProps

export function EventActor({actor, ...props}: EventActorProps): JSX.Element {
  return actor ? <EventActorInternal actor={actor} {...props} /> : <GhostActor />
}

function EventActorInternal({actor, ...props}: EventActorPropsInternal): JSX.Element {
  const {login, avatarUrl} = useFragment(
    graphql`
      fragment EventActor on Actor {
        avatarUrl(size: 64)
        login
      }
    `,
    actor,
  )

  return <EventActorBase login={login} avatarUrl={avatarUrl} {...props} />
}

export function EventActorBase({login, avatarUrl, showAvatarOnly, ...props}: EventActorBaseProps): JSX.Element {
  if (login === VALUES.ghostUserLogin) {
    return <Text>{LABELS.repositoryOwner} </Text>
  } else if (!login) {
    return <Text>{VALUES.ghostUserLogin} </Text>
  }

  return (
    <Box {...props} sx={{display: 'inline-flex', justifyContent: 'center'}}>
      <Link
        href={`/${login}`}
        data-hovercard-url={userHovercardPath({owner: login})}
        sx={{
          color: 'fg.default',
          fontWeight: 600,
          mr: 1,
        }}
        muted
      >
        <GitHubAvatar src={avatarUrl} size={props.largeAvatar ? 24 : 16} sx={{mr: showAvatarOnly ? 0 : 2}} />
        {showAvatarOnly ? '' : login}
      </Link>
    </Box>
  )
}

try{ EventActor.displayName ||= 'EventActor' } catch {}
try{ EventActorInternal.displayName ||= 'EventActorInternal' } catch {}
try{ EventActorBase.displayName ||= 'EventActorBase' } catch {}